import { AppContext } from "@lightspeed-invoicing/core-client/frontend"
import produce from "immer"
import { CombineArrayElements } from "../../../common/Helpers"
import { ProductDtoAttributes } from "../Product"
import { BILLING_METHOD, MATRIX_ITEM_STATUS, PRICING_STRATEGY } from "../Types"
import { MatrixDtoAttributes } from "./Matrix"
import { initialMatrixItem, MatrixItemDto } from "./matrix-item/MatrixItem"
const uuidv4 = require("uuid/v4")

const KEY_SEPARATOR = "_"

type MatchingItem = {
    item: MatrixItemDto
    status?: MATRIX_ITEM_STATUS
}

const productAttributesToCopy = [
    "defaultUnitPrice",
    "defaultUnitCost",
    "multiCurrencyPricing",
    "pricingBrackets",
    "monthlyUnitCost",
    "monthlyUnitPrice",
    "yearlyUnitCost",
    "yearlyUnitPrice",
    
]
export class MatrixItemsMapBuilder {
    private readonly parentTitle: string
    private readonly parentPricingStrategy: PRICING_STRATEGY 
    private readonly parentBillingMethods: BILLING_METHOD[]
    private matrix: MatrixDtoAttributes
    private itemsMap = new Map<string, MatrixItemDto>()
    private namesMap = new Map<string, string>()
    private createAsDeleted: boolean

    constructor(state: ProductDtoAttributes, createAsDeleted: boolean) {
        this.matrix = state.matrix.attributes
        this.parentTitle = state.title
        this.parentPricingStrategy = state.pricingStrategy
        this.parentBillingMethods = state.billingMethods
        this.createAsDeleted = createAsDeleted
    }

    //Generates an Items Map where the keys are all possible combinations of the matrix active options ids
    //and the values are MatrixItemDto
    Initialize(): void {
        const keys = this._generateKeys()
        if (keys.length === 0) return
        keys.forEach(key => {
            this.itemsMap.set(key,
                produce(initialMatrixItem, draft => {
                    const optionIds: string[] = key.split(KEY_SEPARATOR)
                    draft.id = uuidv4()
                    draft.attributes.product.attributes.billingMethods = this.parentBillingMethods
                    draft.attributes.product.attributes.pricingStrategy = this.parentPricingStrategy
                    draft.attributes.product.attributes.title = this._getTitle(key)
                    draft.attributes.product.attributes.currency = AppContext.getAccount().attributes.currency
                    draft.attributes.deleted = this.createAsDeleted

                    optionIds.forEach((optionId: string, axisPosition: number) => {
                        const matrixAxisAttributeName = MatrixItemsMapBuilder._getMatrixAxisAttributeName(axisPosition)
                        if (matrixAxisAttributeName) {
                            draft.attributes[matrixAxisAttributeName] = optionId
                        }
                    })
                })
            )
        })      
    }

    //Based on a matching item, will update if possible existing items attributes or copy some attributes to new items 
    ProvisionExistingItems(): void {
        this.itemsMap.forEach(item => {
            const matchingItem = this.findMatchingItem(
                item.attributes.xMatrixAxisOptionId, 
                item.attributes.yMatrixAxisOptionId, 
                item.attributes.zMatrixAxisOptionId, 
                this.createAsDeleted)
            if(!matchingItem) return

            const key = this._getKey(item)
            this.itemsMap.set(key,
                produce(item, draft => {
                    draft.attributes.productId = matchingItem.status === MATRIX_ITEM_STATUS.NEW ? 
                        "" : matchingItem.item.attributes.productId
                    draft.attributes.product.id = draft.attributes.productId
                    draft.attributes.status_ =  matchingItem.status as MATRIX_ITEM_STATUS
                    draft.attributes.deleted =  matchingItem.item.attributes.deleted

                    // Copy product attributes pricing related 
                    productAttributesToCopy.forEach(attr => {
                        draft.attributes.product.attributes[attr] = matchingItem.item.attributes.product.attributes[attr]
                    } )
                })
            )
            
        })
    }
    
    findMatchingItem(x: string, y: string, z: string, createAsDeleted: boolean): MatchingItem | null {

        //Perfect match
        const existingItem = this.matrix.items.find((i) => {
            return i.attributes.productId && (!x || i.attributes.xMatrixAxisOptionId === x) && 
            (!y ||i.attributes.yMatrixAxisOptionId === y) && 
            (!z || i.attributes.zMatrixAxisOptionId === z)
        })

        if(existingItem) {
            return {
                item: existingItem,
            }
        }

        //if item is new and deleted, not matching item needed
        if(createAsDeleted) return null

        //Partial match
        const closestMatchingItem = this.findClosestMatchingItem(x, y, z)
        if(!closestMatchingItem) return null
        
        const isClosestMatchingItemProvisioned = [ ...this.itemsMap.values()].every(itemMap => {
            return itemMap.attributes.productId !== closestMatchingItem?.attributes.productId
        })

        return {
            item: closestMatchingItem,
            status: !!closestMatchingItem.attributes.productId && isClosestMatchingItemProvisioned ?
                MATRIX_ITEM_STATUS.UPDATED : MATRIX_ITEM_STATUS.NEW
        }
    }
    
    findClosestMatchingItem(x, y, z): MatrixItemDto | undefined {
        const xFiltered = this.matrix.items.filter((i) => i.attributes.xMatrixAxisOptionId === x)
        const xyFiltered = xFiltered.filter((i) => i.attributes.yMatrixAxisOptionId === y)
        const xyzFiltered = xyFiltered.filter((i) => i.attributes.zMatrixAxisOptionId === z)
                
        return xyzFiltered[0] || xyFiltered[0] || xFiltered[0] || this.matrix.items[0]
    }

    GetItems(): MatrixItemDto[] {
        return Array.from(this.itemsMap.values())
    }

    private static _getMatrixAxisAttributeName(axisPosition: number): string {
        switch(axisPosition) {
        case 0: return "xMatrixAxisOptionId"
        case 1: return "yMatrixAxisOptionId"
        case 2: return "zMatrixAxisOptionId"
        default: return ""
        }
    }

    private _generateKeys(): string[] {
        const optionIds: string[][] = []

        this.matrix.axes.map(a => {
            optionIds.push(
                a.options?.map(o => {
                    this.namesMap.set(o.id, o.attributes.name)
                    return o.id
                }) ?? []
            )
        })
        if (optionIds.length === 0) return []
        return CombineArrayElements<string>(KEY_SEPARATOR, ...optionIds)
    }

    private _getKey(item: MatrixItemDto): string {
        const axisIds: string[] = []
        this.matrix.axes.map(a => {
            axisIds.push(item.attributes[`${a.type}MatrixAxisOptionId`])
        })
        return axisIds.join(KEY_SEPARATOR)
    }

    private _getTitle(key: string): string {
        const optionNames: string[] = []
        const optionIds: string[] = key.split(KEY_SEPARATOR)

        optionIds.forEach(key => {
            if (this.namesMap.has(key)) {
                optionNames.push(<string>this.namesMap.get(key))
            }
        })

        return `${this.parentTitle} - ${optionNames.join(" / ")}`
    }

}


// WEBPACK FOOTER //
// ./src/state/product/matrix/MatrixItemsMapBuilder.ts